// 花嫁「裾乱れ」(veilFlurry) を千秋楽で直す (2026-10-09 比較版 → 2026-10-10 ユーザー採用「a」で既定 = a・DEV ?brideFlurry=now|0|off で旧)。
//
//   経緯: 千秋楽が難しすぎる所を直す候補から (台帳 docs/ledger/combat.md 2026-10-09 窓口・昼)。
//     自動プレイで遅れを足すと、速い連 (当たりの間 250ms・連の間 90ms) で最初に崩れる
//     (docs/research/2026-10-09-latency-bot-and-turn-ratio.md)。
//   案と測定 = docs/research/2026-10-09-bride-veil-flurry-fix-options.md。
//
//   この読み込み時に既定 (a) で技の定義を書き換え、?brideFlurry=now|a|b|c|ab で置き直せる。拍の数 (9)・溜め・鐘・絵・当たりの届き・
//   初撃と本命の重さは、どの案でも変えない。既定 = a (配布版)。now / 0 / off = 前のまま (DEV の URL だけ)。
//     a  = 速い連 (1〜6発目) を軽く (当たっても弾いても軽い): 当たった時の体力 8→4・体幹 14→7、
//          普通のパリィで弾いた時の自分の体幹 11→6 (ジャストと同じ)。時間・ボスの体幹の溜まり方は今のまま。
//     b  = 速い連の間を延ばす: 当たりの間 250→320ms (連の間 90→160ms)。傷は今のまま。
//     c  = 速い連を3つずつに区切る: たたた・(500ms)・たたた。間の1か所だけ 250→500ms。傷は今のまま。
//     ab = a と b を両方 (組み合わせの参考)。
//   URL を読むのは game.ts の import.meta.env.DEV の箱の中だけ (配布版は a のまま・ほかの案の切り替えは入らない)。
//   回る絵と風の時計 (src/bride_spin_fx.ts brideSpinClock) は技の間を毎回読み直すので、b と c は回り方も一緒に伸びる。
import { getAttack } from './attacks'
import type { AttackDef } from './types'

export type BrideFlurryMode = 'now' | 'a' | 'b' | 'c' | 'ab'
export const BRIDE_FLURRY_MODES: readonly BrideFlurryMode[] = ['now', 'a', 'b', 'c', 'ab']

const ATTACK_ID = 'veilFlurry'
/** 速い連 = 1〜6発目 (0発目 = 溜めの後の初撃「たん」、7発目 = 振りかぶり、8発目 = 本命「だん」)。 */
export const FAST_HIT_INDICES = [1, 2, 3, 4, 5, 6] as const

/** a: 速い連1発の傷。今は技の基準値 (体力 8・体幹 14) をそのまま使っている。 */
export const LIGHT_FAST_HIT = { damage: 4, postureDamage: 7 } as const
/** a: 速い連を普通のパリィで弾いた時の自分の体幹。今は全技共通の 11 (econ.ts)。ジャスト (PLAYER.perfectParrySelfPostureGain) と同じ 6。 */
export const LIGHT_FAST_NORMAL_PARRY_SELF_GAIN = 6
/** b: 速い連の連の間 (ms)。当たりの長さ 160 と足して 320ms = パリィ1回の長さ (受付 200 + 硬直 120) と同じ。 */
export const WIDE_FAST_GAP_MS = 160
/** c: 3発目と4発目の間だけを延ばす連の間 (ms)。当たりの長さ 160 と足して 500ms。 */
export const PHRASE_BREATH_GAP_MS = 340

type Snapshot = { comboGapsMs: number[]; comboStages: NonNullable<AttackDef['comboStages']> }
let mode: BrideFlurryMode = 'now'   // 読み込みの最後に既定の a へ置く
let saved: Snapshot | null = null

export function parseBrideFlurryMode(raw: unknown): BrideFlurryMode | null {
  const k0 = typeof raw === 'string' ? raw.toLowerCase() : ''
  const k = k0 === '0' || k0 === 'off' ? 'now' : k0   // 0 / off = 前のまま (now と同じ)
  return (BRIDE_FLURRY_MODES as readonly string[]).includes(k) ? k as BrideFlurryMode : null
}

export function brideFlurryMode(): BrideFlurryMode { return mode }

/** URL の値から切り替える。知らない値・指定なしは何もしない = 今のまま。 */
export function setBrideFlurryFromParams(raw: string | null): void {
  const m = parseBrideFlurryMode(raw)
  if (m) setBrideFlurryMode(m)
}

function snapshot(atk: AttackDef): Snapshot {
  return {
    comboGapsMs: [...(atk.comboGapsMs ?? [])],
    comboStages: (atk.comboStages ?? []).map((s) => ({ ...s })),
  }
}

export function setBrideFlurryMode(next: BrideFlurryMode): void {
  const atk = getAttack(ATTACK_ID)
  saved ??= snapshot(atk)
  // まず今の定義へ戻してから、案の分だけ書き換える (案どうしを行き来しても混ざらない)。
  atk.comboGapsMs = [...saved.comboGapsMs]
  atk.comboStages = saved.comboStages.map((s) => ({ ...s }))
  if (next === 'a' || next === 'ab') {
    for (const i of FAST_HIT_INDICES) atk.comboStages[i] = { ...atk.comboStages[i], ...LIGHT_FAST_HIT }
  }
  if (next === 'b' || next === 'ab') {
    for (const i of FAST_HIT_INDICES.slice(0, 5)) atk.comboGapsMs[i] = WIDE_FAST_GAP_MS
  }
  if (next === 'c') {
    atk.comboGapsMs[3] = PHRASE_BREATH_GAP_MS
  }
  mode = next
}

/**
 * 普通のパリィで弾いた時の自分の体幹 (hits.ts から呼ぶ)。a / ab の時の裾乱れ 1〜6発目だけ軽くし、それ以外は base のまま。
 *   hitIndex = 弾いた瞬間のボスの comboHitIndex (当たりはその段の active 中に解決される)。配布版は既定の a = 速い連だけ軽い。
 */
export function brideFlurryNormalParrySelfGain(attackId: string, hitIndex: number, base: number): number {
  if (attackId !== ATTACK_ID || (mode !== 'a' && mode !== 'ab')) return base
  return (FAST_HIT_INDICES as readonly number[]).includes(hitIndex) ? Math.min(base, LIGHT_FAST_NORMAL_PARRY_SELF_GAIN) : base
}

// 既定 (2026-10-10 ユーザー採用) = a。技の定義を読み込み時に書き換える (hits.ts がこのファイルを読むので、ゲームも自動プレイの測定も通る)。
setBrideFlurryMode('a')
